import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import AppShell, { SectionCard } from "../components/AppShell";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import {
  Plus, PaperPlaneTilt, DotsThreeVertical, PencilSimple, User,
  Key, LockOpen, ArrowsClockwise, UserSwitch, Archive, ArrowClockwise,
  Copy, FileText, CurrencyDollar, MagnifyingGlass, ShieldCheck,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY_FORM = { name: "", plan: "growth", industry: "", admin_email: "", admin_name: "", admin_password: "" };
const PLANS = ["starter", "growth", "scale", "enterprise"];
const STATUSES = ["active", "trial", "suspended", "archived"];

export default function Companies() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [q, setQ] = useState("");
  const [editingCompany, setEditingCompany] = useState(null);
  const [changingAdminFor, setChangingAdminFor] = useState(null);

  const load = async () => { const { data } = await api.get("/companies"); setRows(data); };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter(c =>
      (c.name || "").toLowerCase().includes(needle) ||
      (c.industry || "").toLowerCase().includes(needle) ||
      (c.primary_domain || "").toLowerCase().includes(needle) ||
      (c.admin_email || "").toLowerCase().includes(needle) ||
      (c.plan || "").toLowerCase().includes(needle)
    );
  }, [rows, q]);

  const submit = async () => {
    setBusy(true);
    try {
      // Trim password — backend treats blank as "send magic link" path.
      const payload = { ...form, admin_password: form.admin_password.trim() || undefined };
      const { data } = await api.post("/companies", payload);
      const msg = data.invite_method === "magic_link"
        ? `Onboarded. Magic-link sign-in email sent to ${form.admin_email}.`
        : `Onboarded. Temporary-password welcome email sent to ${form.admin_email}.`;
      toast.success(msg);
      setOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const resend = async (row, e) => {
    e?.stopPropagation();
    setResending(row.id);
    try {
      const { data } = await api.post(`/companies/${row.id}/resend-invite`);
      toast.success(data.delivered
        ? `Fresh magic-link invite sent to ${data.email}.`
        : `Invite re-issued for ${data.email} (email delivery may be delayed).`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not resend invite"); }
    finally { setResending(null); }
  };

  const impersonate = async (row) => {
    try {
      const { data } = await api.post(`/companies/${row.id}/impersonate`);
      // Copy the token to clipboard. Full server-side cookie-swap UX ships
      // separately; for now the super_admin opens an incognito window +
      // pastes the token, or uses the console: localStorage.setItem('hrms_token', '<paste>').
      try { await navigator.clipboard.writeText(data.access_token); } catch { /* clipboard blocked */ }
      toast.success(
        `Impersonation token copied for ${data.admin_email} (30 min). ` +
        `Open an incognito window on saarone.com, and in the browser console run ` +
        `localStorage.setItem('hrms_token','<paste>'); location.href='/app'`,
        { duration: 20000 }
      );
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Impersonate failed"); }
  };

  const resetAdminPassword = async (row) => {
    if (!window.confirm(`Send a fresh password-setup link to ${row.name}'s admin and log them out of every device? Any active session on that account will end immediately.`)) return;
    try {
      const { data } = await api.post(`/companies/${row.id}/reset-admin-password`);
      toast.success(`Password reset email sent to ${data.admin_email}. Sessions revoked.`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Reset failed"); }
  };

  const forceRotate = async (row) => {
    try {
      const { data } = await api.post(`/companies/${row.id}/force-password-rotate`);
      toast.success(`${data.admin_email} must rotate password on next login.`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const unlockAdmin = async (row) => {
    try {
      const { data } = await api.post(`/companies/${row.id}/unlock-admin`);
      toast.success(`${data.admin_email} unlocked.`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const archiveCompany = async (row) => {
    if (!window.confirm(`Archive "${row.name}"? All users will lose access immediately. Data is preserved — you can restore later.`)) return;
    try {
      await api.delete(`/companies/${row.id}`);
      toast.success(`${row.name} archived.`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Archive failed"); }
  };

  const restoreCompany = async (row) => {
    try {
      await api.post(`/companies/${row.id}/restore`);
      toast.success(`${row.name} restored.`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Restore failed"); }
  };

  const copyId = async (row) => {
    try {
      await navigator.clipboard.writeText(row.id);
      toast.success(`Copied tenant ID: ${row.id.slice(0, 8)}…`);
    } catch { toast.error("Clipboard blocked by browser"); }
  };

  return (
    <AppShell title="Companies">
      <SectionCard
        title={`${rows.length} tenant companies`}
        subtitle="Onboard a new tenant"
        testid="section-companies"
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-zinc-950 hover:bg-zinc-800 rounded-md" data-testid="new-company-btn"><Plus size={16} className="mr-1.5" /> Onboard company</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Onboard a new company</DialogTitle></DialogHeader>
              <div className="space-y-4" data-testid="company-form">
                <div><Label>Company name</Label><Input className="mt-2" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="c-name" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Plan</Label>
                    <select className="mt-2 w-full h-10 border border-zinc-300 rounded-md px-3 bg-white text-sm" value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} data-testid="c-plan">
                      <option value="starter">Starter</option>
                      <option value="growth">Growth</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </div>
                  <div><Label>Industry</Label><Input className="mt-2" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} data-testid="c-industry" /></div>
                </div>
                <div><Label>HR Admin name</Label><Input className="mt-2" value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} data-testid="c-admin-name" /></div>
                <div><Label>HR Admin email</Label><Input className="mt-2" type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} data-testid="c-admin-email" /></div>
                <div>
                  <Label>Initial password <span className="text-zinc-400 font-normal">(optional)</span></Label>
                  <Input
                    className="mt-2"
                    type="text"
                    placeholder="Leave blank to send a one-time magic link"
                    value={form.admin_password}
                    onChange={(e) => setForm({ ...form, admin_password: e.target.value })}
                    data-testid="c-admin-pw"
                  />
                  <p className="mt-1.5 text-[11px] text-zinc-500 leading-snug">
                    Recommended: leave blank. The admin gets a one-time link to set their own password (valid 24 h). Email is auto-verified when they click it. Fill this only if you need an immediate sign-in (admin will be forced to rotate it on first login).
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button disabled={busy} className="bg-zinc-950 hover:bg-zinc-800" onClick={submit} data-testid="c-submit">{busy ? "Onboarding…" : "Onboard"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      >
        <div className="mb-4">
          <div className="relative max-w-md">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, industry, domain, admin email, plan…"
              className="pl-9 h-9 text-sm"
              data-testid="companies-search"
            />
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Plan</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Industry</TableHead>
              <TableHead className="text-right">Employees</TableHead>
              <TableHead className="text-right w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((c) => (
              <TableRow key={c.id} data-testid={`crow-${c.id}`} className={`cursor-pointer hover:bg-zinc-50 ${c.status === 'archived' ? 'opacity-50' : ''}`} onClick={() => navigate(`/app/companies/${c.id}`)}>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell><Badge variant="outline" className="uppercase text-[10px] tracking-wider">{c.plan}</Badge></TableCell>
                <TableCell>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                    c.status === 'archived' ? 'bg-zinc-100 border-zinc-300 text-zinc-600'
                    : c.status === 'suspended' ? 'bg-red-50 border-red-200 text-red-700'
                    : c.status === 'trial' ? 'bg-blue-50 border-blue-200 text-blue-700'
                    : 'bg-emerald-50 border-emerald-200 text-emerald-700'
                  }`}>{c.status}</span>
                </TableCell>
                <TableCell className="text-zinc-600">{c.industry || "—"}</TableCell>
                <TableCell className="text-right">{c.employee_count}</TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <RowActions
                    company={c}
                    resending={resending === c.id}
                    onResend={(e) => resend(c, e)}
                    onEdit={() => setEditingCompany(c)}
                    onImpersonate={() => impersonate(c)}
                    onResetPw={() => resetAdminPassword(c)}
                    onForceRotate={() => forceRotate(c)}
                    onUnlock={() => unlockAdmin(c)}
                    onChangeAdmin={() => setChangingAdminFor(c)}
                    onArchive={() => archiveCompany(c)}
                    onRestore={() => restoreCompany(c)}
                    onCopyId={() => copyId(c)}
                    onViewAudit={() => navigate(`/app/audit-log?company_id=${c.id}`)}
                    onViewBilling={() => navigate(`/app/companies/${c.id}?tab=billing`)}
                    onOpen={() => navigate(`/app/companies/${c.id}`)}
                  />
                </TableCell>
              </TableRow>
            ))}
            {!filtered.length && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-zinc-500">
                  {q ? `No companies match "${q}"` : "No companies yet."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </SectionCard>

      {editingCompany && (
        <EditCompanyDialog
          company={editingCompany}
          onClose={() => setEditingCompany(null)}
          onSaved={() => { setEditingCompany(null); load(); }}
        />
      )}
      {changingAdminFor && (
        <ChangeAdminDialog
          company={changingAdminFor}
          onClose={() => setChangingAdminFor(null)}
          onDone={() => { setChangingAdminFor(null); load(); }}
        />
      )}
    </AppShell>
  );
}

function RowActions({ company, resending, onResend, onEdit, onImpersonate, onResetPw,
                     onForceRotate, onUnlock, onChangeAdmin, onArchive, onRestore,
                     onCopyId, onViewAudit, onViewBilling, onOpen }) {
  const isArchived = company.status === "archived";
  const invitePending = company.admin_state === "invite_pending";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`row-actions-${company.id}`}>
          <DotsThreeVertical size={18} weight="bold" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56" data-testid={`row-actions-menu-${company.id}`}>
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-zinc-500">Overview</DropdownMenuLabel>
        <DropdownMenuItem onClick={onOpen} data-testid={`ra-open-${company.id}`}>
          <User size={14} className="mr-2" /> Open tenant
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onEdit} data-testid={`ra-edit-${company.id}`}>
          <PencilSimple size={14} className="mr-2" /> Edit tenant
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onImpersonate} disabled={isArchived}>
          <ShieldCheck size={14} className="mr-2" /> Impersonate admin (30 min)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onViewAudit}>
          <FileText size={14} className="mr-2" /> View audit log
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onViewBilling}>
          <CurrencyDollar size={14} className="mr-2" /> Billing &amp; usage
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onCopyId}>
          <Copy size={14} className="mr-2" /> Copy tenant ID
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-zinc-500">Admin lifecycle</DropdownMenuLabel>
        {invitePending && (
          <DropdownMenuItem onClick={onResend} disabled={resending}>
            <PaperPlaneTilt size={14} className="mr-2" /> {resending ? "Sending…" : "Resend invite"}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={onResetPw} disabled={isArchived}>
          <Key size={14} className="mr-2" /> Reset admin password
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onForceRotate} disabled={isArchived}>
          <ArrowsClockwise size={14} className="mr-2" /> Force password rotate
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onUnlock} disabled={isArchived}>
          <LockOpen size={14} className="mr-2" /> Unlock admin
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onChangeAdmin} disabled={isArchived}>
          <UserSwitch size={14} className="mr-2" /> Change primary admin
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {isArchived ? (
          <DropdownMenuItem onClick={onRestore} className="text-emerald-700">
            <ArrowClockwise size={14} className="mr-2" /> Restore tenant
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem onClick={onArchive} className="text-red-700">
            <Archive size={14} className="mr-2" /> Archive tenant
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function EditCompanyDialog({ company, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: company.name || "",
    industry: company.industry || "",
    plan: company.plan || "growth",
    primary_domain: company.primary_domain || "",
    timezone: company.timezone || "Asia/Kolkata",
    status: company.status || "active",
    brand_color: company.brand_color || "",
    logo_url: company.logo_url || "",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/companies/${company.id}`, form);
      toast.success(`${form.name} updated.`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Update failed"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="edit-company-dialog">
        <DialogHeader><DialogTitle>Edit {company.name}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="ec-name" /></div>
          <div><Label>Industry</Label><Input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} /></div>
          <div><Label>Plan</Label>
            <select className="h-9 w-full rounded-md border border-zinc-300 px-2 text-sm" value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })}>
              {PLANS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div><Label>Primary domain</Label><Input value={form.primary_domain} placeholder="acme.io" onChange={(e) => setForm({ ...form, primary_domain: e.target.value })} /></div>
          <div><Label>Timezone</Label><Input value={form.timezone} placeholder="Asia/Kolkata" onChange={(e) => setForm({ ...form, timezone: e.target.value })} /></div>
          <div><Label>Status</Label>
            <select className="h-9 w-full rounded-md border border-zinc-300 px-2 text-sm" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div><Label>Brand color</Label><Input value={form.brand_color} placeholder="#4f46e5" onChange={(e) => setForm({ ...form, brand_color: e.target.value })} /></div>
          <div className="col-span-2"><Label>Logo URL</Label><Input value={form.logo_url} placeholder="https://…" onChange={(e) => setForm({ ...form, logo_url: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={saving} onClick={save} data-testid="ec-save">{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChangeAdminDialog({ company, onClose, onDone }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [demote, setDemote] = useState("employee");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!email.trim() || !email.includes("@")) return toast.error("Valid email required");
    setSaving(true);
    try {
      const { data } = await api.post(`/companies/${company.id}/change-admin`, {
        new_admin_email: email.trim().toLowerCase(),
        new_admin_name: name.trim() || null,
        demote_previous_to: demote,
      });
      toast.success(
        data.new_admin_user_created
          ? `Setup link emailed to ${data.new_admin_email}. Previous admin (${data.previous_admin_email || "none"}) demoted to ${demote}.`
          : `${data.new_admin_email} promoted to primary admin.`
      );
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Change failed"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="change-admin-dialog">
        <DialogHeader><DialogTitle>Change primary admin — {company.name}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2 text-sm">
          <div><Label>New admin email</Label><Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="newadmin@company.com" data-testid="ca-email"/></div>
          <div><Label>New admin name (optional)</Label><Input value={name} onChange={(e) => setName(e.target.value)}/></div>
          <div><Label>Demote previous admin to</Label>
            <select className="h-9 w-full rounded-md border border-zinc-300 px-2 text-sm" value={demote} onChange={(e) => setDemote(e.target.value)}>
              <option value="employee">Employee</option>
              <option value="branch_manager">Branch Manager</option>
              <option value="inactive">Deactivate (no login)</option>
            </select>
          </div>
          <p className="text-[11px] text-zinc-500">If the new email already exists as a user of this tenant, they&apos;ll be promoted. Otherwise a fresh setup-link is emailed to them.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={saving} onClick={submit} data-testid="ca-submit">{saving ? "Saving…" : "Change admin"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
